import { useMemo } from 'react';
import { MultiSelect, PriceBandIcon, Prompt, Table, Typography } from '@weareopenr/component-library';
import { usePricingCategoryFilter } from 'src/hooks/usePricingCategoryFilter/usePricingCategoryFilter';
import styles from './PriceSchemeRulesTab.module.scss';
import { PromptActions } from 'src/constants';
import { getChannelLabel, getPricingChannels } from 'src/helpers/channels/getPricingChannels';
import { useRules } from 'src/hooks/useRules/useRules';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { getRulesTableColumns } from './Table';
import SkeletonContainer from '../SkeletonContainer';
import { IAggregator } from 'src/types/aggregator.types';
import { IPriceRule } from '../../types/price.types';
import { TTableRow, TPricingCategoryRow } from './types';
import { AdjustmentType, RoundingType } from '@weareopenr/types';
import { IPriceBandWithVenues } from '../../types/priceBand.types';
import { usePriceBands } from '../../hooks/usePriceBands';
import { PriceBandRuleModal, PriceBandRuleSteps } from '../PriceBandRuleModal';
import { usePricingCategories } from 'src/hooks/usePricingCategories';
import { INestedPricingCategory, IPricingCategory } from 'src/api/getPricingCategories/types';
import { CategoryOverrideRuleModal } from '../CategoryOverrideRuleModal';
import { UNCATEGORISED_ID } from 'src/utils/pricingCategories';
import { ChannelRuleModal } from '../ChannelRuleModal';
import { useChannelRuleActions } from './useChannelRuleActions';
import { usePriceBandActions } from './usePriceBandActions';
import {
  usePriceBandExternalSystemFilter,
  DEFAULT_PRICE_BAND_FILTER_ID,
} from 'src/hooks/usePriceBandExternalSystemFilter';
import { findInheritedRule } from '../../utils/priceRules/findInheritedRule';
import { usePricingCategoryOverrideActions } from './usePricingCategoryOverrideActions';

const NO_CHANNELS_MESSAGE = 'No channels exist for this brand';
const NO_PRICE_BANDS_MESSAGE = 'No price bands exist for this price scheme';

const buildCategoryRows = (
  parentId: string,
  parentType: 'price_band' | 'extension_instance',
  categories: INestedPricingCategory[],
  priceRules: IPriceRule[],
  categoryMap: Map<string, IPricingCategory>,
): TPricingCategoryRow[] =>
  categories.map((cat) => {
    const overrideRule = priceRules.find((rule) => {
      const rawCategoryId = rule.pricing_category?.id ?? rule.pricing_category_id;
      if (rawCategoryId === undefined) return false;
      const categoryId = rawCategoryId ?? UNCATEGORISED_ID;
      const catId = cat.category_id ?? UNCATEGORISED_ID;

      if (categoryId !== catId) {
        return false;
      } else if (parentType === 'price_band') {
        return rule.price_band?.id === parentId;
      } else {
        return rule.extension_instance?.id === parentId;
      }
    });

    const inheritedRule = findInheritedRule(cat, parentId, parentType, priceRules, categoryMap, overrideRule);

    return {
      _type: 'pricing_category' as const,
      category: cat,
      parentId,
      parentType,
      overrideRule,
      inheritedRule,
      children:
        cat.category_id !== null && cat.children.length > 0
          ? buildCategoryRows(parentId, parentType, cat.children, priceRules, categoryMap)
          : undefined,
    };
  });

const filterCategoryRows = (rows: TPricingCategoryRow[], selectedIds: string[]): TPricingCategoryRow[] =>
  rows.reduce<TPricingCategoryRow[]>((acc, row) => {
    const id = row.category.category_id ?? UNCATEGORISED_ID;
    if (selectedIds.includes(id)) {
      acc.push(row);
    } else if (row.children) {
      const filteredChildren = filterCategoryRows(row.children, selectedIds);
      if (filteredChildren.length > 0) {
        acc.push({ ...row, children: filteredChildren });
      }
    }
    return acc;
  }, []);

const buildRules = (
  priceRules: IPriceRule[],
  externalSystems: IAggregator[],
  priceBands: IPriceBandWithVenues[],
  isCreatingInline: boolean,
  categories: INestedPricingCategory[],
  flatCategories: IPricingCategory[] = [],
): TTableRow[] => {
  const pricingChannels = getPricingChannels(externalSystems);

  const priceBandRules = priceRules.filter(
    (rule) => rule.price_band && !rule.pricing_category && !rule.pricing_category_id,
  );
  const channelRules = priceRules.filter(
    (rule) => rule.extension_instance && !rule.pricing_category && !rule.pricing_category_id,
  );

  const categoryMap = new Map<string, IPricingCategory>(
    flatCategories.filter((cat) => cat.category_id).map((cat) => [cat.category_id ?? '', cat]),
  );

  const joinedPriceBandRules = priceBands.map((priceBand) => {
    const matchedRule = priceBandRules?.find((rule) =>
      rule.price_band ? rule.price_band.id === priceBand.priceBandId : false,
    );

    const base: IPriceRule = matchedRule ?? {
      price_rule_id: `${priceBand.priceBandId}_rule`,
      price_band: {
        name: priceBand.name,
        id: priceBand.priceBandId,
      },
      adjustment: { type: AdjustmentType.NONE },
      rounding: { type: RoundingType.NONE },
    };

    return {
      ...base,
      ...(base.price_band && { price_band: { ...base.price_band, name: priceBand.name } }),
      venueCount: priceBand?.venues?.length,
      venues: priceBand?.venues ?? [],
      categoryRows:
        priceBand?.priceBandId === null
          ? undefined
          : buildCategoryRows(priceBand.priceBandId, 'price_band', categories, priceRules, categoryMap),
    };
  });

  const joinedChannelRules = pricingChannels.map((channel) => {
    const rule = channelRules.find((rule) => rule.extension_instance.id === channel.extensionInstanceId);
    const channelName = getChannelLabel(channel);

    const base: IPriceRule = rule ?? {
      price_rule_id: `${channel.extensionInstanceId}_rule`,
      extension_instance: {
        name: channelName,
        id: channel.extensionInstanceId,
      },
      adjustment: { type: AdjustmentType.NONE },
      rounding: { type: RoundingType.NONE },
    };

    return {
      ...base,
      // One external system can back several instances, so the row is labelled per instance and
      // keeps the system name only for resolving the avatar.
      extension_instance: { ...base.extension_instance, name: channelName },
      externalSystemName: channel.name,
      categoryRows: buildCategoryRows(
        channel.extensionInstanceId,
        'extension_instance',
        categories,
        priceRules,
        categoryMap,
      ),
    };
  });

  const priceBandSection: TTableRow[] = [
    { _type: 'descriptive' as const, label: 'Price Bands' },
    ...(joinedPriceBandRules.length > 0
      ? joinedPriceBandRules
      : [{ _type: 'empty' as const, message: NO_PRICE_BANDS_MESSAGE }]),
    ...(isCreatingInline ? [{ _type: 'inline-create' as const }] : [{ _type: 'create-band-cta' as const }]),
  ];

  const channelSection: TTableRow[] = [
    { _type: 'descriptive' as const, label: 'Channels' },
    ...(joinedChannelRules.length > 0
      ? joinedChannelRules
      : [{ _type: 'empty' as const, message: NO_CHANNELS_MESSAGE }]),
  ];

  return [...priceBandSection, ...channelSection];
};

export const PriceSchemeRulesTab = ({ priceSchemeId }: { priceSchemeId: string }) => {
  const { data: externalSystems, isLoading: isExternalSystemsLoading } = useExternalSystems();
  const { data: priceRules = [], isLoading: isRulesLoading } = useRules(priceSchemeId);
  const { priceBands = [], isLoading: isPriceBandsLoading } = usePriceBands(priceSchemeId);
  const {
    nestedCategories: categories,
    orderedFlatCategories,
    isLoading: isCategoriesLoading,
  } = usePricingCategories(priceSchemeId);
  const isLoadingData = isRulesLoading || isExternalSystemsLoading || isPriceBandsLoading || isCategoriesLoading;

  const {
    editingChannelRule,
    deletingChannelRule,
    setDeletingChannelRule,
    activeChannel,
    handleCreateClick,
    handleChannelEditClick,
    handleDeleteClick,
    resetEditingChannelRule,
    handleDelete,
  } = useChannelRuleActions(priceRules, priceSchemeId);

  const {
    isCreatingInline,
    editingPriceBandId,
    venueModalPriceBand,
    setVenueModalPriceBand,
    deletingPriceBand,
    setDeletingPriceBand,
    editingRule,
    priceBandToEdit,
    isDeleting,
    handleStartCreate,
    handleEditName,
    handleClose,
    handleSubmit,
    handleEditVenues,
    handlePriceBandDelete,
    handleConfirmDelete,
    resetEditingRule,
    handleEditClick,
  } = usePriceBandActions(priceSchemeId, priceBands);

  const {
    hasPricingCategoryFilterFeature,
    pricingCategoryOptions,
    searchPricingCategories,
    setSearchPricingCategories,
  } = usePricingCategoryFilter(priceSchemeId);
  const {
    overrideModal,
    setOverrideModal,
    handleAddOverride,
    setOverrideToDelete,
    handleEditOverride,
    resetEditOverride,
    editingOverride,
    overrideToDelete,
    handleDeleteOverride,
  } = usePricingCategoryOverrideActions(priceSchemeId, priceBands, priceRules);
  const { selectedViewIds, setSelectedViewIds, viewFilterOptions } = usePriceBandExternalSystemFilter(
    priceBands,
    externalSystems ?? [],
  );

  const tableRows = useMemo(() => {
    if (isLoadingData) return [];
    const rows = buildRules(
      priceRules,
      externalSystems,
      priceBands,
      isCreatingInline,
      categories,
      orderedFlatCategories,
    );

    let filtered = rows;
    if (selectedViewIds.length > 0) {
      filtered = rows.filter((row) => {
        if ('_type' in row) return true;
        else if ('price_band' in row && row.price_band)
          return selectedViewIds.includes(row.price_band.id ?? DEFAULT_PRICE_BAND_FILTER_ID);
        else if ('extension_instance' in row && row.extension_instance)
          return selectedViewIds.includes(row.extension_instance.id);
        return true;
      });
    }

    if (searchPricingCategories.length === 0) return filtered;
    return filtered.map((row) => {
      if ('categoryRows' in row && row.categoryRows) {
        return { ...row, categoryRows: filterCategoryRows(row.categoryRows, searchPricingCategories) };
      }
      return row;
    });
  }, [
    priceRules,
    externalSystems,
    priceBands,
    isCreatingInline,
    categories,
    orderedFlatCategories,
    searchPricingCategories,
    selectedViewIds,
  ]);

  const columns = useMemo(
    () =>
      getRulesTableColumns({
        nameForm: {
          onStartCreate: handleStartCreate,
          onSubmit: handleSubmit,
          onClose: handleClose,
          existingNames: priceBands.map(({ name }) => name),
          onEditName: handleEditName,
          editingPriceBandId,
        },
        priceBandActions: {
          onEditVenues: handleEditVenues,
          onDelete: handlePriceBandDelete,
          onEdit: handleEditClick,
        },
        channelActions: {
          handleCreate: handleCreateClick,
          handleEdit: handleChannelEditClick,
          handleDelete: handleDeleteClick,
        },
        priceCategoryOverrideActions: {
          onAddOverride: handleAddOverride,
          handleEdit: handleEditOverride,
          handleDelete: setOverrideToDelete,
        },
      }),
    [editingPriceBandId, isCreatingInline, priceBands],
  );

  if (isLoadingData) {
    return <SkeletonContainer hiddenTitle />;
  }

  const categoryRulesForParent = overrideModal
    ? priceRules.filter((r) => {
        if (r.pricing_category == null && r.pricing_category_id == null) return false;
        return overrideModal.parentType === 'price_band'
          ? r.price_band?.id === overrideModal.parentId
          : r.extension_instance?.id === overrideModal.parentId;
      })
    : [];

  return (
    <>
      <Prompt
        promptHeading="Are you sure you want to delete this channel rule?"
        closePrompt={() => setDeletingChannelRule(null)}
        open={deletingChannelRule !== null}
        buttonHandler={() => handleDelete(deletingChannelRule)}
        buttonText={PromptActions.DELETE}
        buttonColour="tomato"
        backButtonText={PromptActions.CANCEL}
        backButton={true}
        size="small"
      >
        You will permanently remove the calculation and rounding rules assigned to this channel.
      </Prompt>

      <Prompt
        promptHeading={`Are you sure you want to delete the override rule on ${overrideToDelete?.name}?`}
        closePrompt={() => setOverrideToDelete(null)}
        open={overrideToDelete !== null}
        buttonHandler={() => handleDeleteOverride(overrideToDelete.price_rule_id)}
        buttonText={PromptActions.DELETE}
        buttonColour="tomato"
        backButtonText={PromptActions.CANCEL}
        backButton={true}
        size="small"
      >
        <Typography>
          Deleting this rule will remove it from this price band. Any child categories that currently inherit it will no
          longer inherit this rule.
        </Typography>
        <Typography size="small" className={styles['delete-sub-text']}>
          This action cannot be undone
        </Typography>
      </Prompt>

      <div className={styles['rules-list']}>
        <div className={styles['filters']}>
          {viewFilterOptions.length > 0 && (
            <MultiSelect
              label="Table View"
              Icon={PriceBandIcon}
              options={viewFilterOptions}
              selected={selectedViewIds}
              onChange={setSelectedViewIds}
              showClearButton
            />
          )}
          {hasPricingCategoryFilterFeature && pricingCategoryOptions.length > 0 && (
            <MultiSelect
              Icon={PriceBandIcon}
              label="Pricing Categories"
              options={pricingCategoryOptions}
              selected={searchPricingCategories}
              onChange={setSearchPricingCategories}
            />
          )}
        </div>
        <Table
          data={tableRows}
          columns={columns}
          maxHeight="63vh"
          getSubRows={(row) => {
            if ('categoryRows' in row) {
              return row.categoryRows;
            } else if ('children' in row && row._type === 'pricing_category') {
              return row.children;
            } else {
              return undefined;
            }
          }}
        />
        <PriceBandRuleModal
          priceBandToEdit={priceBandToEdit}
          priceSchemeId={priceSchemeId}
          open={editingRule.step !== null}
          step={editingRule.step}
          closeModal={resetEditingRule}
          priceBands={priceBands}
        />
        {overrideModal && (
          <CategoryOverrideRuleModal
            open={!!overrideModal}
            closeModal={() => setOverrideModal(null)}
            priceSchemeId={priceSchemeId}
            defaultCategoryId={overrideModal.categoryId}
            parentId={overrideModal.parentId}
            parentName={overrideModal.parentName}
            parentType={overrideModal.parentType}
            rules={categoryRulesForParent}
          />
        )}
        <CategoryOverrideRuleModal
          open={editingOverride.rule !== null}
          closeModal={resetEditOverride}
          priceSchemeId={priceSchemeId}
          ruleToEdit={editingOverride.rule}
          step={editingOverride.step}
        />
        <Prompt
          promptHeading={`Are you sure you want to delete "${deletingPriceBand?.name}" price band?`}
          open={deletingPriceBand !== null}
          closePrompt={() => setDeletingPriceBand(null)}
          buttonHandler={handleConfirmDelete}
          buttonText="Delete"
          buttonColour="tomato"
          backButtonText={PromptActions.CANCEL}
          backButton
          size="small"
          isSubmitting={isDeleting}
        >
          This action will delete the price band and all associated rules.
        </Prompt>
        <PriceBandRuleModal
          open={venueModalPriceBand !== null}
          closeModal={() => setVenueModalPriceBand(null)}
          priceSchemeId={priceSchemeId}
          priceBandToEdit={venueModalPriceBand}
          priceBands={priceBands as IPriceBandWithVenues[]}
          step={PriceBandRuleSteps.Venues}
        />
      </div>

      <ChannelRuleModal
        priceRuleToEdit={activeChannel}
        priceSchemeId={priceSchemeId}
        open={editingChannelRule.step !== null}
        step={editingChannelRule.step}
        preSelectedChannelId={editingChannelRule.newRuleChannelId}
        closeModal={resetEditingChannelRule}
      />
    </>
  );
};
